import React, { useEffect } from "react";
import { useUser } from "../contexts/useUser";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMCallsRecursive } from "../dataaccess/fetchLLMCalls";
import { fetchOpenRouterCatalog } from "../dataaccess/fetchOpenRouterCatalog";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

interface CostDashboardProps {
  // Add any props you need here
}

const CostDashboard: React.FC<CostDashboardProps> = () => {
  const { project, entitlements, trialDaysRemaining } = useUser();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["calls", { projectId: project?.id }],
    queryFn: () =>
      fetchLLMCallsRecursive(project?.id ?? "", 1000, true, "", "", 30),

    enabled: project?.id ? true : false,
  });

  const { data: openRouterCatalog } = useQuery({
    queryKey: ["openrouter-catalog"],
    queryFn: () => fetchOpenRouterCatalog(),
    enabled: project?.id ? true : false,
  });

  const callsData = data?.data?.callsByProjectIdAndCreatedAt?.items ?? [];

  // Replace the useEffect block with useMemo below:

  const aggregatedCosts = React.useMemo(() => {
    if (!project || !callsData || callsData.length === 0) {
      return {
        total: 0,
        savings: 0,
        openAI: 0,
        microsoft: 0,
        google: 0,
        anthropic: 0,
        amazon: 0,
        mistral: 0,
        totalCostAtGPT4o: 0,
        customerCosts: {} as {
          [key: string]: { name: string; totalCost: number };
        },
      };
    }

    let total = 0;
    let savings = 0;
    let openAI = 0;
    let microsoft = 0;
    let google = 0;
    let anthropic = 0;
    let amazon = 0;
    let mistral = 0;
    let totalCostAtGPT4o = 0;
    const customerCostsTemp: {
      [key: string]: { name: string; totalCost: number };
    } = {};

    const openRouterPricingByModelId = new Map<
      string,
      { inputPerMillion: number; outputPerMillion: number; providerId: string }
    >();

    (openRouterCatalog || []).forEach((record) => {
      const modelId = record?.model?.id;
      if (!modelId) {
        return;
      }

      const promptNano = Number(record.currentPricing?.promptTokenUsdNano || 0);
      const completionNano = Number(
        record.currentPricing?.completionTokenUsdNano || 0
      );

      const inputPerMillion = Number.isFinite(promptNano) ? promptNano / 1000 : 0;
      const outputPerMillion = Number.isFinite(completionNano)
        ? completionNano / 1000
        : 0;

      openRouterPricingByModelId.set(modelId, {
        inputPerMillion,
        outputPerMillion,
        providerId: record.model.providerId || "",
      });
    });

    // Compute averaged token prices for calls that are not "chaos"
    let averageInputTokenPrice = 0;
    let averageOutputTokenPrice = 0;
    const nonChaosCalls = callsData.filter(
      (call) =>
        call?.status !== "302" &&
        ((call?.service &&
          call?.service.vendor !== "chaos" &&
          call?.service.inputTokenPricePerMillion &&
          call?.service.inputTokenPricePerMillion > 0 &&
          call?.service.outputTokenPricePerMillion &&
          call?.service.outputTokenPricePerMillion > 0) ||
          (call?.model &&
            (call?.model?.inputTokenPrice ?? 0) > 0 &&
            (call?.model?.outputTokenPrice ?? 0) > 0) ||
          (call?.modelId &&
            (openRouterPricingByModelId.get(call.modelId)?.inputPerMillion ?? 0) > 0 &&
            (openRouterPricingByModelId.get(call.modelId)?.outputPerMillion ?? 0) > 0))
    );

    nonChaosCalls.forEach((call) => {
      const openRouterPricing = call?.modelId
        ? openRouterPricingByModelId.get(call.modelId)
        : undefined;

      averageInputTokenPrice +=
        (call?.service?.inputTokenPricePerMillion ?? 0) +
        (call?.model?.inputTokenPrice ?? 0) +
        (openRouterPricing?.inputPerMillion ?? 0);
      averageOutputTokenPrice +=
        (call?.service?.outputTokenPricePerMillion ?? 0) +
        (call?.model?.outputTokenPrice ?? 0) +
        (openRouterPricing?.outputPerMillion ?? 0);
    });

    if (nonChaosCalls.length > 0) {
      averageInputTokenPrice /= nonChaosCalls.length;
      averageOutputTokenPrice /= nonChaosCalls.length;
    }

    callsData.forEach((call) => {
      const inputTokens = call?.tokenCountPrompt ?? 0;
      const outputTokens = call?.tokenCountResponse ?? 0;

      const openRouterPricing = call?.modelId
        ? openRouterPricingByModelId.get(call.modelId)
        : undefined;

      const inputTokenPricePerMillion = call?.service
        ? call?.service?.inputTokenPricePerMillion ?? 0
        : call?.model
        ? call?.model?.inputTokenPrice ?? 0
        : openRouterPricing
        ? openRouterPricing.inputPerMillion
        : averageInputTokenPrice;

      const outputTokenPricePerMillion = call?.service
        ? call?.service?.outputTokenPricePerMillion ?? 0
        : call?.model
        ? call?.model?.outputTokenPrice ?? 0
        : openRouterPricing
        ? openRouterPricing.outputPerMillion
        : averageOutputTokenPrice;

      const cost =
        (inputTokens * inputTokenPricePerMillion +
          outputTokens * outputTokenPricePerMillion) /
        1000000;

      total += cost;

      if (call?.status === "302") {
        savings += cost;
      } else if (
        call?.service?.vendor === "openai" ||
        call?.model?.host?.toLowerCase() === "openai" ||
        openRouterPricing?.providerId === "openai"
      ) {
        openAI += cost;
      } else if (
        call?.service?.vendor === "microsoft" ||
        call?.model?.host?.toLowerCase() === "microsoft" ||
        openRouterPricing?.providerId === "microsoft"
      ) {
        microsoft += cost;
      } else if (
        call?.service?.vendor === "google" ||
        call?.model?.host?.toLowerCase() === "google" ||
        openRouterPricing?.providerId === "google"
      ) {
        google += cost;
      } else if (
        call?.service?.vendor === "anthropic" ||
        call?.model?.host?.toLowerCase() === "anthropic" ||
        openRouterPricing?.providerId === "anthropic"
      ) {
        anthropic += cost;
      } else if (
        call?.service?.vendor === "amazon" ||
        call?.model?.host?.toLowerCase() === "amazon" ||
        openRouterPricing?.providerId === "amazon"
      ) {
        amazon += cost;
      } else if (
        call?.service?.vendor === "mistral" ||
        call?.model?.host?.toLowerCase() === "mistral" ||
        openRouterPricing?.providerId === "mistral"
      ) {
        mistral += cost;
      }

      totalCostAtGPT4o += (inputTokens * 2.5 + outputTokens * 10) / 1000000;

      if (
        call?.customerId &&
        call?.customer?.name &&
        !(call?.service?.vendor === "chaos")
      ) {
        if (!customerCostsTemp[call.customerId]) {
          customerCostsTemp[call.customerId] = {
            name: call?.customer?.name ?? "Unknown",
            totalCost: 0,
          };
        }
        customerCostsTemp[call.customerId].totalCost += cost;
      }
    });

    return {
      total,
      savings,
      openAI,
      microsoft,
      google,
      anthropic,
      amazon,
      mistral,
      totalCostAtGPT4o,
      customerCosts: customerCostsTemp,
    };
  }, [project, callsData, openRouterCatalog]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error...</div>;
  }

  const truncateName = (name: string, characters = 10) => {
    return name.length > 10 ? `${name.substring(0, characters)}...` : name;
  };

  const sortedCustomers = Object.entries(aggregatedCosts.customerCosts).sort(
    (a, b) => b[1].totalCost - a[1].totalCost
  );
  const top10Customers = sortedCustomers.slice(0, 10);
  const otherCustomers = sortedCustomers.slice(10);

  const otherTotalCost = otherCustomers.reduce(
    (acc, [, customer]) => acc + customer.totalCost,
    0
  );

  const totalCostCustomers = sortedCustomers.reduce(
    (acc, [, customer]) => acc + customer.totalCost,
    0
  );

  return (
    <Stack>
      <Typography sx={{ ml: 2, mt: 2 }} variant="h6">
        Cost breakdown for last 30 days
      </Typography>
      <Box display="flex" flexDirection="row" gap={2}>
        <Tooltip title="Cost for OpenAI requests in the last 30 days">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            m={2}
          >
            <Typography variant="h6" align="center">
              OpenAI
            </Typography>
            <Typography variant="h5" align="center">
              ${aggregatedCosts.openAI.toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              Total cost for OpenAI
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Cost for Microsoft requests in the last 30 days. Microsoft don't return token counts for streaming responses, so we estimate the tokens until they implement this feature.">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            m={2}
          >
            <Typography variant="h6" align="center">
              Microsoft
            </Typography>
            <Typography variant="h5" align="center">
              ${aggregatedCosts.microsoft.toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              Total cost for Microsoft
              <br />
              (estimated)
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Cost for Google requests in the last 30 days">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            m={2}
          >
            <Typography variant="h6" align="center">
              Google
            </Typography>
            <Typography variant="h5" align="center">
              ${aggregatedCosts.google.toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              Total cost for Google
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Cost for Anthropic requests in the last 30 days">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            m={2}
          >
            <Typography variant="h6" align="center">
              Anthropic
            </Typography>
            <Typography variant="h5" align="center">
              ${aggregatedCosts.anthropic.toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              Total cost for Anthropic
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Cost for Amazon requests in the last 30 days">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            m={2}
          >
            <Typography variant="h6" align="center">
              Amazon
            </Typography>
            <Typography variant="h5" align="center">
              ${aggregatedCosts.amazon.toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              Total cost for Amazon
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Cost for Mistral requests in the last 30 days">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            m={2}
          >
            <Typography variant="h6" align="center">
              Mistral
            </Typography>
            <Typography variant="h5" align="center">
              ${aggregatedCosts.mistral.toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              Total cost for Mistral
            </Typography>
          </Box>
        </Tooltip>
      </Box>
      <Box display="flex" flexDirection="row" gap={2}>
        <Tooltip title="Total cost is the sum of all costs for all services in the last 30 days. Remember vendor costs change often so update them in the specific service">
          <Box
            m={2}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="h6" align="center">
              Total Cost
            </Typography>
            <Typography variant="h5" align="center">
              ${aggregatedCosts.total.toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              Total cost all services
            </Typography>
          </Box>
        </Tooltip>

        <Tooltip title="Total cost IF ALL prompts sent to GPT4o (reference)">
          <Box
            m={2}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="h6" align="center">
              Reference Cost
            </Typography>
            <Typography variant="h5" align="center">
              ${aggregatedCosts.totalCostAtGPT4o.toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              Cost if all calls sent to GPT4o
            </Typography>
          </Box>
        </Tooltip>

        <Tooltip title="Cost saved by Routing to lower cost models where appropriate. This is estimated using the GPT4o cost versus the actual input and output costs for services in the most recent 100 calls">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            m={2}
          >
            <Typography variant="h6" align="center">
              Routing Saved
            </Typography>
            <Typography variant="h5" align="center">
              - $
              {(
                aggregatedCosts.totalCostAtGPT4o - aggregatedCosts.total
              ).toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              {(
                ((aggregatedCosts.totalCostAtGPT4o - aggregatedCosts.total) /
                  aggregatedCosts.totalCostAtGPT4o) *
                100
              ).toFixed(0)}
              % savings
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Cost saved by response caching. This is estimated using the average input and output costs for services in the most recent 100 calls">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            m={2}
          >
            <Typography variant="h6" align="center">
              Caching Saved
            </Typography>
            <Typography variant="h5" align="center">
              - ${aggregatedCosts.savings.toFixed(3)}
            </Typography>
            <Typography variant="caption" align="center">
              {(
                (aggregatedCosts.savings / aggregatedCosts.total) *
                100
              ).toFixed(0)}
              % savings
              <br />
              (avg cost all services)
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      <Box>
        <Typography sx={{ ml: 2, mt: 2 }} variant="h6">
          Top Customers by Cost{" "}
          <Chip
            size="small"
            label="prod / ent"
            title="Analytics by customer is available at production and enterprise levels"
            color={
              trialDaysRemaining > 0 ||
              entitlements?.includes("customer-analytics")
                ? "default"
                : "warning"
            }
          />{" "}
        </Typography>
        {trialDaysRemaining > 0 ||
        entitlements?.includes("customer-analytics") ? (
          <Box display="flex" flexDirection="column" gap={2} m={2}>
            {top10Customers.map(([customerId, customer]) => (
              <Box
                key={customerId}
                display="flex"
                flexDirection="row"
                alignItems="center"
              >
                <Tooltip
                  title={`${customer.name} total cost for last 30 days. Click to edit.`}
                >
                  <Typography
                    variant="body1"
                    sx={{ minWidth: 200, width: 200 }}
                    onClick={() => {
                      navigate("customers/edit/" + customerId);
                    }}
                  >
                    {truncateName(customer.name, 20)}
                  </Typography>
                </Tooltip>
                <Typography variant="body1" sx={{ minWidth: 100, width: 100 }}>
                  ${customer.totalCost.toFixed(3)}
                </Typography>
                <Box
                  sx={{
                    height: 20,
                    width: `${
                      (customer.totalCost / totalCostCustomers) * 100
                    }%`,
                    backgroundColor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <Typography variant="caption">
                    {customer.totalCost / totalCostCustomers > 0.01
                      ? (
                          (customer.totalCost / totalCostCustomers) *
                          100
                        ).toFixed(0) + "%"
                      : ""}
                  </Typography>
                </Box>
              </Box>
            ))}
            {otherCustomers.length > 0 && (
              <Box display="flex" flexDirection="row" alignItems="center">
                <Tooltip title="Total cost for other customers in the last 30 days.">
                  <Typography
                    variant="body1"
                    sx={{ minWidth: 200, width: 200 }}
                  >
                    Others
                  </Typography>
                </Tooltip>
                <Typography variant="body1" sx={{ minWidth: 100, width: 100 }}>
                  ${otherTotalCost.toFixed(3)}
                </Typography>
                <Box
                  sx={{
                    height: 20,
                    width: `${(otherTotalCost / aggregatedCosts.total) * 100}%`,
                    backgroundColor: "primary.main",
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            sx={{ m: 2 }}
          >
            <Tooltip title="Total token call cost for customers in the last 30 days.">
              <Typography
                variant="body1"
                sx={{ minWidth: 200, width: 200, color: "text.disabled" }}
              >
                Example customer
              </Typography>
            </Tooltip>
            <Typography
              variant="body1"
              sx={{ minWidth: 100, width: 100, color: "text.disabled" }}
            >
              $123.45
            </Typography>
            <Box
              sx={{
                height: 20,
                width: `25%`,
                backgroundColor: "text.disabled",
              }}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default CostDashboard;

/*
      <Box display="flex" flexDirection="row" gap={2}>
        {Object.entries(customerCosts)
          .sort((a, b) => b[1].totalCost - a[1].totalCost)
          .slice(0, 10)
          .map(([customerId, customer], index) => (
            <Tooltip title={`Cost for ${customer.name} in the last 30 days`}>
              <Box
                key={customerId}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                m={2}
              >
                <Typography variant="h6" align="center">
                  {index + 1}. {truncateName(customer.name)}
                </Typography>
                <Typography variant="h5" align="center">
                  ${customer.totalCost.toFixed(3)}
                </Typography>
                <Typography variant="caption" align="center">
                  last 30 days
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => {
                    navigate("customers/edit/" + customerId);
                  }}
                >
                  Edit
                </Button>
              </Box>
            </Tooltip>
          ))}
      </Box>*/
